import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { PostActionDialog } from './PostActionDialog';
import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

export interface PostActionDialogContentApi {
  close: () => void;
}

export type PostActionDialogContent = ReactNode | ((api: PostActionDialogContentApi) => ReactNode);

export interface StandalonePostActionDialogProps extends StandaloneComponentProps<typeof PostActionDialog> {
  content?: PostActionDialogContent;
  dialog?: ComponentProps<typeof PostActionDialog.Dialog>;
  queryParamTrigger?: string;
}

export const StandalonePostActionDialog: StandaloneComponent<StandalonePostActionDialogProps> = ({
  content,
  dialog,
  queryParamTrigger = 'payment_accepted',
  ...props
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [showDialog, setShowDialog] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (searchParams.get(queryParamTrigger)) {
      setShowDialog(true);

      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete(queryParamTrigger);

      const newUrl = pathname + (newSearchParams.toString() ? '?' + newSearchParams.toString() : '');
      window.history.pushState(null, '', newUrl);
    }
  }, [searchParams, router, pathname]);

  useEffect(() => {
    if (showDialog && dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, [showDialog]);

  const resolvedContent =
    typeof content === 'function' ? content({ close: () => dialogRef.current?.close() }) : content;

  return (
    <PostActionDialog {...props}>
      <PostActionDialog.Dialog
        {...dialog}
        content={resolvedContent ?? dialog?.content}
        options={{ ...dialog?.options, ref: dialogRef }}
      />
    </PostActionDialog>
  );
};
